import { useEffect, useMemo, useState } from 'react';
import { s } from '../listPageStyles';
import { MultiChipSelect } from './MultiChipSelect';
import {
  CONDITION_UNITS, QUANTITY_UNITS, PRODUCT_TARGET_TYPES,
  csvToArr, arrToCsv, resolveLabels,
} from './campaignUtils';
import { RELATION_CONFIGS } from '../relationConfigs';

const CONFIGS = {
  PRODUCT: RELATION_CONFIGS.product,
  GROUP: RELATION_CONFIGS.productGroup,
  SUBGROUP: RELATION_CONFIGS.productSubGroup,
  DYNAMIC: RELATION_CONFIGS.productDynamicGroup,
};

// CSV <-> chip item köprüsü; etiketleri verilen tanım endpoint'inden çözer.
function useCsvItems(csv, setCsv, endpoint) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const ids = csvToArr(csv);
    if (!ids.length) { setItems([]); return; }
    let alive = true;
    resolveLabels(endpoint, ids).then((map) => {
      if (alive) setItems(ids.map((id) => ({ value: id, label: map[id] || id })));
    }).catch(() => { if (alive) setItems(ids.map((id) => ({ value: id, label: id }))); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csv, endpoint]);
  const onChange = (next) => { setItems(next); setCsv(arrToCsv(next.map((x) => x.value))); };
  return [items, onChange];
}

export function CampaignProductTargetTab({ form, setForm, isEdit }) {
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));
  const inputStyle = isEdit ? s.input : s.inputView;
  const selectStyle = isEdit ? s.select : s.selectView;
  const isAmount = form.conditionUnit === 'AMOUNT';
  const isVariety = form.conditionUnit === 'VARIETY';

  // #8 — Genel sekmesindeki kampanya tipine göre ödül alanları aktif/pasif:
  //   İndirim (DISCOUNT_PCT) → "İndirim %" + "Sabit İndirim (₺)" aktif, "Bedava miktar" pasif.
  //   Paket / Bedelsiz (BUNDLE / FREE_GIFT) → "Bedava miktar" aktif, indirim alanları pasif.
  const isDiscount = form.type === 'DISCOUNT_PCT';
  const isFreeOrBundle = form.type === 'FREE_GIFT' || form.type === 'BUNDLE';
  const discountEditable = isEdit && isDiscount;
  const freeEditable = isEdit && isFreeOrBundle;

  // Aktif ürün hedef türü — dolu olan CSV'den türetilir (yoksa PRODUCT).
  const initialType = csvToArr(form.applicableDynamicGroupIdsCsv).length ? 'DYNAMIC'
    : csvToArr(form.applicableSubGroupIdsCsv).length ? 'SUBGROUP'
      : csvToArr(form.applicableGroupIdsCsv).length ? 'GROUP' : 'PRODUCT';
  const [targetType, setTargetType] = useState(initialType);

  const activeMeta = useMemo(() => PRODUCT_TARGET_TYPES.find((t) => t.value === targetType) || PRODUCT_TARGET_TYPES[0], [targetType]);
  const [scopeItems, setScopeItems] = useCsvItems(form[activeMeta.csvField], (v) => set({ [activeMeta.csvField]: v }), activeMeta.endpoint);
  const [freeItems, setFreeItems] = useCsvItems(form.freeProductIdsCsv, (v) => set({ freeProductIdsCsv: v }), '/api/products');

  const switchType = (next) => {
    if (!isEdit || next === targetType) return;
    // Tek kapsam aktif olsun: diğer tür CSV'lerini temizle.
    const clear = {};
    PRODUCT_TARGET_TYPES.forEach((t) => { if (t.value !== next) clear[t.csvField] = ''; });
    set(clear);
    setTargetType(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h3 className="cmp-section-title">🎯 Ürün Hedeflemesi</h3>
        <p className="cmp-section-sub">Bu promosyonun uygulanacağı ürün kapsamı ve ödül parametreleri.</p>
      </div>

      {/* Ürün hedef türü — kart seçimi (Müşteriler sekmesindeki gibi).
          auto-fill: kart sayısından bağımsız sabit boyut → Müşteriler sekmesiyle birebir aynı kutucuk boyutu. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
        {PRODUCT_TARGET_TYPES.map((t) => {
          const active = targetType === t.value;
          return (
            <div
              key={t.value}
              role="button"
              onClick={() => switchType(t.value)}
              style={{ ...typeCard, ...(active ? typeCardActive : null), cursor: isEdit ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }} aria-hidden="true">{t.icon}</span>
                <span style={{ fontWeight: 800, fontSize: 14 }}>{t.label}</span>
                {active && <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 800 }}>✓</span>}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary,#6b7280)', marginTop: 6 }}>{t.desc}</div>
            </div>
          );
        })}
      </div>

      {/* Seçilen türe göre çoklu seçim listesi (Müşteriler sekmesiyle aynı panel) */}
      <div style={panel}>
        <h5 style={panelTitle}>
          {activeMeta.label} Listesi
          {!isEdit && !scopeItems.length && <span style={{ color: 'var(--text-secondary,#6b7280)', fontWeight: 600 }}> (Tümü)</span>}
        </h5>
        <MultiChipSelect
          key={targetType}
          items={scopeItems}
          onChange={setScopeItems}
          config={CONFIGS[targetType]}
          placeholder={`${activeMeta.label} ara ve ekle…`}
          disabled={!isEdit}
        />
        {form.type === 'FREE_GIFT' && (
          <div style={{ marginTop: 14 }}>
            <div style={summaryLabel}>Hediye / Bedelsiz Ürünler</div>
            <div style={{ marginTop: 6 }}>
              <MultiChipSelect items={freeItems} onChange={setFreeItems} config={RELATION_CONFIGS.product} placeholder="Hediye ürün ara ve ekle…" disabled={!isEdit} />
            </div>
          </div>
        )}
      </div>

      {/* Promosyon Parametreleri (panel) */}
      <div style={panel}>
        <h5 style={panelTitle}>🎁 Promosyon Parametreleri</h5>
        <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>Koşul Birimi</label>
          {isEdit
            ? <select style={selectStyle} value={form.conditionUnit} onChange={(e) => set({ conditionUnit: e.target.value })}>{CONDITION_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}</select>
            : <input style={inputStyle} readOnly value={(CONDITION_UNITS.find((u) => u.value === form.conditionUnit) || {}).label || form.conditionUnit} />}
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>
            {isAmount ? 'Alınması Gereken Tutar (₺)' : isVariety ? 'Gereken Çeşit Sayısı' : 'Alınması Gereken Miktar'}
          </label>
          {isAmount ? (
            <input style={inputStyle} type="number" min="0" step="0.01" value={form.thresholdAmount} readOnly={!isEdit} onChange={(e) => set({ thresholdAmount: e.target.value })} />
          ) : isVariety ? (
            // Çeşit bazlı: kademeler TiersCsv'de; eşik = çeşit sayısı (birim yok).
            <input style={inputStyle} type="number" min="0" value={form.thresholdQty} readOnly={!isEdit} onChange={(e) => set({ thresholdQty: e.target.value })} />
          ) : (
            // Miktar bazlıysa: miktar + birim seçimi yan yana.
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputStyle, flex: 1 }} type="number" min="0" value={form.thresholdQty} readOnly={!isEdit} onChange={(e) => set({ thresholdQty: e.target.value })} />
              {isEdit
                ? <select style={{ ...selectStyle, width: 110 }} value={form.quantityUnit || 'Adet'} onChange={(e) => set({ quantityUnit: e.target.value })}>{QUANTITY_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}</select>
                : <input style={{ ...inputStyle, width: 110 }} readOnly value={form.quantityUnit || 'Adet'} />}
            </div>
          )}
        </div>
      </div>

      {/* Ödül alanları — kampanya tipine göre aktif/pasif (#8) */}
      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={{ ...s.label, opacity: freeEditable || !isEdit ? 1 : 0.5 }}>Bedava Verilecek Miktar</label>
          <input
            style={freeEditable ? s.input : disabledInput}
            type="number" min="0" value={form.freeQty}
            readOnly={!freeEditable}
            onChange={(e) => set({ freeQty: e.target.value })}
          />
          {isEdit && !isFreeOrBundle && <span style={hint}>Yalnızca Paket / Bedelsiz tipinde</span>}
        </div>
        <div style={s.formGroup}>
          <label style={{ ...s.label, opacity: discountEditable || !isEdit ? 1 : 0.5 }}>İndirim %</label>
          <input
            style={discountEditable ? s.input : disabledInput}
            type="number" min="0" max="100" step="0.01" value={form.discountPct}
            readOnly={!discountEditable}
            onChange={(e) => set({ discountPct: e.target.value })}
          />
          {isEdit && !isDiscount && <span style={hint}>Yalnızca İndirim tipinde</span>}
        </div>
        <div style={s.formGroup}>
          <label style={{ ...s.label, opacity: discountEditable || !isEdit ? 1 : 0.5 }}>Sabit İndirim (₺)</label>
          <input
            style={discountEditable ? s.input : disabledInput}
            type="number" min="0" step="0.01" value={form.discountAmount}
            readOnly={!discountEditable}
            onChange={(e) => set({ discountAmount: e.target.value })}
          />
          {isEdit && !isDiscount && <span style={hint}>Yalnızca İndirim tipinde</span>}
        </div>
      </div>

      <div style={s.formGroup}>
        <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: isEdit ? 'pointer' : 'default' }}>
          <input type="checkbox" checked={!!form.isPreConditional} disabled={!isEdit} onChange={(e) => set({ isPreConditional: e.target.checked })} />
          Ön koşullu (eşik doluncaya kadar uygulanmaz)
        </label>
        </div>
      </div>
    </div>
  );
}

const typeCard = {
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 14, padding: '12px 14px',
  background: 'var(--bg-card,#fff)', transition: 'all 120ms',
};
const typeCardActive = {
  borderColor: 'var(--accent)', background: 'rgba(0,115,234,0.06)', boxShadow: '0 4px 14px rgba(0,115,234,0.12)',
};
const panel = {
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 16, padding: 18, background: 'var(--bg-card,#fff)',
};
const panelTitle = { margin: '0 0 12px', fontSize: 14, fontWeight: 800 };
const summaryLabel = { fontSize: 12, color: 'var(--text-secondary,#6b7280)', fontWeight: 600 };
const disabledInput = {
  width: '100%', padding: '9px 12px', borderRadius: 10, fontSize: 14, boxSizing: 'border-box',
  border: '1px solid var(--border-color,#e5e7eb)', background: 'var(--bg-secondary,#f1f5f9)',
  color: 'var(--text-secondary,#9ca3af)', cursor: 'not-allowed',
};
const hint = { fontSize: 11.5, color: 'var(--text-secondary,#9ca3af)', marginTop: 4, display: 'inline-block' };

export default CampaignProductTargetTab;
